import CardHeader from "components/CardHeader";
import PdfDocumentViewer from "components/PdfDocumentViewer";
import { AccountAttachment, AccountAttachmentRequirement } from "models/Account";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner
} from "reactstrap";
import {
    deleteAccountAttachment,
    getAccountAttachment,
    getAccountAttachmentContent,
    getAccountAttachments,
    getAttachmentRequirements,
    reviewAccountAttachment,
    updateAccountAttachmentExpiration
} from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { OfficeTranslationKey } from "i18n/officeTranslations";

const statusColor = (status: AccountAttachment["review_status"]) =>
    status === "approved" ? "success" : status === "rejected" ? "danger" : "warning";

const statusKey: Record<AccountAttachment["review_status"], OfficeTranslationKey> = {
    approved: "accounts.accountAttachmentsCard.statusApproved",
    pending_approval: "accounts.accountAttachmentsCard.statusPending",
    rejected: "accounts.accountAttachmentsCard.statusRejected",
};

interface PreviewAccountAttachment extends AccountAttachment { preview_url?: string }

const AccountAttachmentsCard: React.FC<{ accountId: string; tenantId: string }> = ({ accountId, tenantId }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [attachments, setAttachments] = useState<AccountAttachment[]>([]);
    const [requirements, setRequirements] = useState<AccountAttachmentRequirement[]>([]);
    const [busy, setBusy] = useState("");
    const [error, setError] = useState<OfficeTranslationKey | "">("");
    const [previewAttachment, setPreviewAttachment] = useState<AccountAttachment | null>(null);
    const [previewContent, setPreviewContent] = useState<PreviewAccountAttachment | null>(null);
    const [previewError, setPreviewError] = useState<OfficeTranslationKey | "">("");
    const openedDeepLink = useRef("");
    const [rejectionAttachment, setRejectionAttachment] = useState<AccountAttachment | null>(null);
    const [rejectionNote, setRejectionNote] = useState("");
    const [rejectionError, setRejectionError] = useState<OfficeTranslationKey | "">("");
    const [deleteAttachment, setDeleteAttachment] = useState<AccountAttachment | null>(null);
    const [expiryAttachment, setExpiryAttachment] = useState<AccountAttachment | null>(null);
    const [expiryDate, setExpiryDate] = useState("");
    const [expiryError, setExpiryError] = useState<OfficeTranslationKey | "">("");

    const load = () => Promise.all([
        getAccountAttachments(accountId),
        getAttachmentRequirements(tenantId),
    ]).then(([attachmentValues, requirementValues]) => {
        setAttachments(attachmentValues);
        setRequirements(requirementValues);
        setError("");
    }).catch(() => setError("accounts.accountAttachmentsCard.loadFailed"));

    useEffect(() => { load(); }, [accountId, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => {
        if (previewContent?.preview_url) URL.revokeObjectURL(previewContent.preview_url);
    }, [previewContent]);

    const requirementNames = useMemo(
        () => Object.fromEntries(requirements.map(value => [value.id, value.name])),
        [requirements]
    );

    const review = async (attachment: AccountAttachment, status: "approved" | "rejected", note = "") => {
        setBusy(attachment.id);
        setError("");
        try {
            await reviewAccountAttachment(accountId, attachment.id, status, note);
            await load();
            setRejectionAttachment(null);
            setRejectionNote("");
        } catch {
            if (status === "rejected") {
                setRejectionError("accounts.accountAttachmentsCard.rejectionSaveFailed");
            } else {
                setError("accounts.accountAttachmentsCard.approvalFailed");
            }
        } finally {
            setBusy("");
        }
    };

    const reject = () => {
        const note = rejectionNote.trim();
        if (!rejectionAttachment || !note) {
            setRejectionError("accounts.accountAttachmentsCard.rejectionReasonRequired");
            return;
        }
        setRejectionError("");
        review(rejectionAttachment, "rejected", note);
    };

    const open = async (attachment: AccountAttachment) => {
        setPreviewAttachment(attachment);
        setPreviewContent(null);
        setPreviewError("");
        try {
            const complete = await getAccountAttachment(accountId, attachment.id);
            if (!complete.content && !complete.document_id) {
                setPreviewError("accounts.accountAttachmentsCard.noContent");
                return;
            }
            let previewUrl: string | undefined;
            if (!complete.content) {
                const content = await getAccountAttachmentContent(accountId, attachment.id);
                previewUrl = URL.createObjectURL(content);
            }
            setPreviewContent({...complete, preview_url: previewUrl});
        } catch {
            setPreviewError("accounts.accountAttachmentsCard.openFailed");
        }
    };

    const deepLinkAttachmentId = searchParams.get("attachment") ?? "";
    useEffect(() => {
        if (!deepLinkAttachmentId) {
            openedDeepLink.current = "";
            return;
        }
        if (openedDeepLink.current === deepLinkAttachmentId) return;
        const attachment = attachments.find(value => value.id === deepLinkAttachmentId);
        if (!attachment) return;
        openedDeepLink.current = deepLinkAttachmentId;
        open(attachment);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachments, deepLinkAttachmentId]);

    const closePreview = () => {
        setPreviewAttachment(null);
        setPreviewContent(null);
        setPreviewError("");
        if (searchParams.get("attachment")) {
            const next = new URLSearchParams(searchParams);
            next.delete("attachment");
            setSearchParams(next, { replace: true });
        }
    };

    const remove = async () => {
        if (!deleteAttachment) return;
        setBusy(deleteAttachment.id);
        setError("");
        try {
            await deleteAccountAttachment(accountId, deleteAttachment.id);
            setDeleteAttachment(null);
            await load();
        } catch {
            setError("accounts.accountAttachmentsCard.deleteFailed");
        } finally {
            setBusy("");
        }
    };

    const editExpiry = (attachment: AccountAttachment) => {
        setExpiryAttachment(attachment);
        setExpiryDate(attachment.expires_at?.slice(0, 10) ?? "");
        setExpiryError("");
    };

    const saveExpiry = async () => {
        if (!expiryAttachment) return;
        setBusy(expiryAttachment.id);
        setExpiryError("");
        try {
            await updateAccountAttachmentExpiration(accountId, expiryAttachment.id, expiryDate || undefined);
            setExpiryAttachment(null);
            await load();
        } catch {
            setExpiryError("accounts.accountAttachmentsCard.expirySaveFailed");
        } finally {
            setBusy("");
        }
    };

    const previewData = previewContent?.content
        ? `data:${previewContent.content_type};base64,${previewContent.content}`
        : previewContent?.preview_url ?? "";

    return <>
        <Card id="account-attachments">
            <CardBody>
                <CardHeader title={t("accounts.accountAttachmentsCard.documents")} icon="mdi-folder-multiple-outline" count={attachments.length} />
                {error && <Alert color="danger" className="mt-3 mb-0">{t(error)}</Alert>}
                {attachments.length === 0 && !error && <p className="text-muted mt-3 mb-0">{t("accounts.accountAttachmentsCard.noDocumentsUploadedYet")}</p>}
                {attachments.map(attachment => <div key={attachment.id} className="border-bottom py-3">
                    <div className="d-flex justify-content-between gap-2">
                        <div>
                            <strong>{requirementNames[attachment.requirement_id] ?? t("accounts.accountAttachmentsCard.unknownType")}</strong>
                            <div className="text-muted small">{attachment.name}</div>
                            {attachment.expires_at && <div className="small">{t("accounts.accountAttachmentsCard.expiresDate", {date: new Intl.DateTimeFormat(intlLocale, {dateStyle: "medium"}).format(new Date(attachment.expires_at))})}</div>}
                            {attachment.review_note && <div className="small text-danger">{attachment.review_note}</div>}
                        </div>
                        <Badge color={statusColor(attachment.review_status)} className="align-self-start">
                            {t(statusKey[attachment.review_status])}
                        </Badge>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-3">
                        <Button size="sm" color="light" disabled={busy === attachment.id} onClick={() => open(attachment)}>{t("accounts.accountAttachmentsCard.view")}</Button>
                        {attachment.review_status === "pending_approval" && <>
                            <Button size="sm" color="success" disabled={busy === attachment.id} onClick={() => review(attachment, "approved")}>{t("accounts.accountAttachmentsCard.approve")}</Button>
                            <Button size="sm" color="outline-danger" disabled={busy === attachment.id} onClick={() => {
                                setRejectionAttachment(attachment);
                                setRejectionNote("");
                                setRejectionError("");
                            }}>{t("accounts.accountAttachmentsCard.reject")}</Button>
                        </>}
                        <Button size="sm" color="light" disabled={busy === attachment.id} onClick={() => editExpiry(attachment)}>
                            <i className="mdi mdi-calendar-edit me-1" />{t("accounts.accountAttachmentsCard.editExpiry")}
                        </Button>
                        <Button size="sm" color="danger" outline disabled={busy === attachment.id}
                            onClick={() => setDeleteAttachment(attachment)}>
                            <i className="mdi mdi-delete-outline me-1" />{t("common.delete")}
                        </Button>
                        {busy === attachment.id && <Spinner size="sm" />}
                    </div>
                </div>)}
            </CardBody>
        </Card>

        <Modal isOpen={previewAttachment !== null} toggle={closePreview} size="xl" scrollable>
            <ModalHeader toggle={closePreview}>{previewAttachment?.name}</ModalHeader>
            <ModalBody>
                {!previewContent && !previewError && <div className="text-center py-5"><Spinner /></div>}
                {previewError && <Alert color="danger">{t(previewError)}</Alert>}
                {previewContent?.content_type === "application/pdf" && <PdfDocumentViewer
                        content={previewContent.content}
                        url={previewContent.content ? undefined : previewData}
                        contentType={previewContent.content_type}
                        loading={<div className="text-center py-5"><Spinner /></div>}
                        error={<Alert color="danger">{t("accounts.accountAttachmentsCard.thePdfCouldNotBeDisplayed")}</Alert>}
                    />}
                {previewContent?.content_type.startsWith("image/") && <img
                    src={previewData}
                    alt={previewContent.name}
                    className="img-fluid d-block mx-auto"
                />}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={closePreview}>{t("common.close")}</Button>
                {previewContent && previewData && <Button
                    tag="a"
                    color="primary"
                    href={previewData}
                    download={previewContent.name}
                    className="d-inline-flex align-items-center"
                    style={{ minHeight: 44 }}
                >
                    <i className="mdi mdi-download me-1" />{t("accounts.accountAttachmentsCard.download")}
                </Button>}
            </ModalFooter>
        </Modal>

        <Modal isOpen={rejectionAttachment !== null} toggle={() => setRejectionAttachment(null)}>
            <ModalHeader toggle={() => setRejectionAttachment(null)}>{t("accounts.accountAttachmentsCard.rejectDocument")}</ModalHeader>
            <ModalBody>
                <p>{t("accounts.accountAttachmentsCard.explainWhyNameIsRejectedThisReasonIsShownToTheWorker", {name: rejectionAttachment?.name ?? ""})}</p>
                {rejectionError && <div className="text-danger mb-3">{t(rejectionError)}</div>}
                <FormGroup className="mb-0">
                    <Label for="attachment-rejection-note">{t("accounts.accountAttachmentsCard.reasonForRejection")}</Label>
                    <Input
                        id="attachment-rejection-note"
                        type="textarea"
                        rows={4}
                        value={rejectionNote}
                        onChange={event => setRejectionNote(event.target.value)}
                        autoFocus
                    />
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={() => setRejectionAttachment(null)} disabled={busy !== ""}>{t("common.cancel")}</Button>
                <Button color="danger" onClick={reject} disabled={busy !== "" || rejectionNote.trim() === ""}>
                    {busy ? <Spinner size="sm" /> : t("accounts.accountAttachmentsCard.reject")}
                </Button>
            </ModalFooter>
        </Modal>

        <Modal isOpen={deleteAttachment !== null} toggle={() => setDeleteAttachment(null)}>
            <ModalHeader toggle={() => setDeleteAttachment(null)}>{t("accounts.accountAttachmentsCard.deleteDocument")}</ModalHeader>
            <ModalBody>
                {t("common.deleteNameThisCannotBeUndone", {name: deleteAttachment?.name ?? ""})}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={() => setDeleteAttachment(null)} disabled={busy !== ""}>{t("common.cancel")}</Button>
                <Button color="danger" onClick={remove} disabled={busy !== ""}>
                    {busy ? <Spinner size="sm" /> : t("common.delete")}
                </Button>
            </ModalFooter>
        </Modal>

        <Modal isOpen={expiryAttachment !== null} toggle={() => setExpiryAttachment(null)}>
            <ModalHeader toggle={() => setExpiryAttachment(null)}>{t("accounts.accountAttachmentsCard.editExpiryDate")}</ModalHeader>
            <ModalBody>
                <p>{t("accounts.accountAttachmentsCard.updateTheExpiryDateForName", {name: expiryAttachment?.name ?? ""})}</p>
                {expiryError && <div className="text-danger mb-3">{t(expiryError)}</div>}
                <FormGroup className="mb-0">
                    <Label for="attachment-expiry-date">{t("accounts.accountAttachmentsCard.expiryDate")}</Label>
                    <Input
                        id="attachment-expiry-date"
                        type="date"
                        value={expiryDate}
                        onChange={event => setExpiryDate(event.target.value)}
                        autoFocus
                    />
                    <small className="text-muted">{t("accounts.accountAttachmentsCard.leaveThisEmptyToRemoveTheExpiryDate")}</small>
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={() => setExpiryAttachment(null)} disabled={busy !== ""}>{t("common.cancel")}</Button>
                <Button color="primary" onClick={saveExpiry} disabled={busy !== ""}>
                    {busy ? <Spinner size="sm" /> : t("common.save")}
                </Button>
            </ModalFooter>
        </Modal>
    </>;
};

export default AccountAttachmentsCard;
